import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { githubApi } from "@/lib/api/github";
import { teamKeys } from "./use-teams";

export const githubKeys = {
  /** Prefix shared by every status query; invalidate with this. */
  statusAll: ["github", "status"] as const,
  status: (returnTo?: string) => ["github", "status", returnTo ?? null] as const,
  repositories: ["github", "repositories"] as const,
  project: (teamId: string) => ["github", "project", teamId] as const,
};

/**
 * Whether GitHub is connected for the organization (and, for admins, the
 * install link). With `returnTo`, the install link brings the admin back there.
 */
export function useGitHubStatus(returnTo?: string, enabled = true) {
  return useQuery({
    queryKey: githubKeys.status(returnTo),
    queryFn: async () => (await githubApi.status(returnTo)).data,
    enabled,
  });
}

/** A member asks the organization admins to connect GitHub for a project. */
export function useRequestGitHubAccess() {
  return useMutation({ mutationFn: githubApi.requestAccess });
}

/**
 * Repositories the CompBox GitHub App can use. Refetched whenever the window
 * regains focus, so a repo just added to the app on GitHub shows up.
 */
export function useGitHubRepositories(enabled: boolean) {
  return useQuery({
    queryKey: githubKeys.repositories,
    queryFn: async () => (await githubApi.repositories()).data,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: "always",
  });
}

/** A project's repository, including its GitHub link and default branch. */
export function useProjectRepository(teamId: string, enabled = true) {
  return useQuery({
    queryKey: githubKeys.project(teamId),
    queryFn: async () => (await githubApi.projectRepository(teamId)).data,
    enabled,
  });
}

export function useCompleteGitHubInstall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: githubApi.completeInstall,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: githubKeys.statusAll });
      void queryClient.invalidateQueries({ queryKey: githubKeys.repositories });
    },
  });
}

export function useRemoveGitHubInstallation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: githubApi.removeInstallation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["github"] });
    },
  });
}

function useProjectRepoMutation<I>(teamId: string, fn: (input: I) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: githubKeys.project(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

export function useConnectGitHubRepository(teamId: string) {
  return useProjectRepoMutation(teamId, (input: { installationId: string; fullName: string }) =>
    githubApi.connectRepository(teamId, input)
  );
}

export function useCreateGitHubRepository(teamId: string) {
  return useProjectRepoMutation(teamId, (input: { installationId: string; name: string; private: boolean; description?: string }) =>
    githubApi.createRepository(teamId, input)
  );
}
